import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import MentalityOverlay from '../../modules/mentorship/MentalityOverlay';
import { FocusProvider } from '../../modules/mentorship/focus/useFocusContext';
import { PermissionsProvider } from '../auth/PermissionsContext';
import { useCmdKLauncher } from '../command/useCmdKLauncher';
import HelpPill from '../help/HelpPill';
import { useHelpMenu } from '../help/useHelpMenu';

// HelpMenuPanel + SpotlightTourDriver + CmdKPalette lazy-mount per
// LCP wave 3. All three add weight to the entry bundle:
//   - HelpMenuPanel pulls Fuse.js for the in-menu search.
//   - SpotlightTour pulls the step-graph + portal chrome.
//   - CmdKPalette pulls Fuse.js + the pages/mentees/glossary indices
//     for the fuzzy command search.
// Each has a clear out-of-band trigger gating the first paint:
//   - HelpMenuPanel renders null until `controller.open === true`.
//   - SpotlightTourDriver self-gates on the completed sentinel + flag.
//   - CmdKPalette is mounted only while `cmdkLauncher.isOpen` — the
//     tiny `useCmdKLauncher` hook (eager) owns the Cmd-K hotkey + open
//     state on its own; the heavy palette + Fuse chunk loads on the
//     FIRST press and stays cached for subsequent opens.
// Suspense fallback is `null` because none is visible chrome on first
// paint — deferring them off the LCP critical path is free.
const HelpMenuPanel = lazy(() =>
    import('../help/HelpMenu').then((m) => ({ default: m.HelpMenuPanel })),
);
const SpotlightTourDriver = lazy(() =>
    import('../tour/SpotlightTourDriver').then((m) => ({
        default: m.SpotlightTourDriver,
    })),
);
const CmdKPalette = lazy(() => import('../command/CmdKPalette'));
import type { OnboardingRole } from '../../modules/mentorship/onboarding/useOnboardingProgress';
import { isMentor, isMentee } from '../../userService';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { captureError } from '../errors/captureError';
import { useFlag } from '../flags/useFlag';
import { navigateHash } from '../navigation/navigateHash';
import type { TabType } from '../../core/navigation/tabMetadata';
import { type BackendUserWithPlan, resolveRole } from '../../userService';
import type { GlossaryTermKey } from '../../modules/mentorship/glossary';
import type { MenteeLike } from '../command/cmdKIndex';
import './Layout.css';

export type LayoutChildProps = {
    showMenu: boolean;
    onMenuClick: () => void;
};

type LayoutProps = {
    children: (props: LayoutChildProps) => React.ReactNode;
    activeTab: TabType;
    onTabChange: (tab: TabType, subTab?: string) => void;
    educationSubTab?: string | undefined;
    networkingSubTab?: string | undefined;
    userData: BackendUserWithPlan | null;
    onChatSelect?: ((projectId: string, chatId: string) => void) | undefined;
    onNewDirectChat?: (() => void) | undefined;
    selectedChatId?: string | undefined;
    selectedProjectId?: string | undefined;
};

function Layout({
    children,
    activeTab,
    onTabChange,
    educationSubTab,
    networkingSubTab,
    userData,
    onChatSelect,
    onNewDirectChat,
    selectedChatId,
    selectedProjectId,
}: LayoutProps) {
    // WS-DISC-3 — Global Help Menu. Mount-gated on the flag so the
    // pill + slide-over land dark and graduate per the registry's
    // ramp annotation. ContextSidebar's OnboardingChecklist mount
    // self-suppresses on the inverse of this flag (see ContextSidebar.tsx).
    const helpMenuEnabled = useFlag('help-menu-v1');
    // WS-FTUE-1 Phase 2 — Spotlight tour driver. The driver auto-opens
    // the 'mentor-first-run' tour for fresh mentors; the cohort gate is
    // just `isMentor(userData)` now that the flag has graduated to
    // default-on. The driver self-gates on the completed sentinel +
    // resume cursor so re-mounts after a finished walkthrough render
    // nothing.
    const mentorFirstRunEligible = isMentor(userData);
    const helpMenu = useHelpMenu();
    // Derive the onboarding role from the viewer so the migrated
    // OnboardingChecklist inside HelpMenu sees the same role + userId
    // pair that ContextSidebar used to thread through.
    const helpOnboardingRole: OnboardingRole | undefined = isMentor(userData)
        ? 'mentor'
        : isMentee(userData)
            ? 'mentee'
            : undefined;
    const viewerRole = useMemo(() => resolveRole(userData) ?? null, [userData]);
    // Glossary entries open via the existing #glossary hash route;
    // each term-key flows through the URL hash SSOT so deep-links
    // from a palette pick and a copy-link share the same surface.
    const openGlossaryTerm = useCallback((term: GlossaryTermKey) => {
        navigateHash(`#glossary/${term}`);
    }, []);
    // Mentees are wired by the WS-NAV-1 follow-up EntityPicker
    // (out of scope for this foundation commit). Pass an empty list
    // so the palette renders Pages + Glossary + Recents today; the
    // contract surface stays stable when mentees land later.
    const emptyMentees = useMemo<ReadonlyArray<MenteeLike>>(() => [], []);

    // Cmd-K launcher — owns the Cmd-K hotkey + open state on the entry
    // bundle. The heavy CmdKPalette (Fuse + indices + modal chrome)
    // lazy-loads on the FIRST press; the launcher's `isOpen` gates the
    // mount, and `external={{ isOpen, close }}` keeps the palette's
    // own useCmdK in lockstep so dismissal paths (ESC, overlay click,
    // entry pick) propagate up to unmount the lazy chunk.
    const cmdkLauncher = useCmdKLauncher();
    const cmdkExternal = useMemo(
        () => ({ isOpen: cmdkLauncher.isOpen, close: cmdkLauncher.close }),
        [cmdkLauncher.isOpen, cmdkLauncher.close],
    );

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem('ui_sidebarCollapsed') === 'true';
        } catch (err) {
            captureError(err, { scope: 'Layout', op: 'read-sidebar-collapsed' });
            return false;
        }
    });
    const toggleSidebar = (collapsed: boolean) => {
        setSidebarCollapsed(collapsed);
        try {
            localStorage.setItem('ui_sidebarCollapsed', String(collapsed));
        } catch (err) {
            captureError(err, { scope: 'Layout', op: 'persist-sidebar-collapsed' });
        }
    };

    return (
        // PermissionsProvider mounted ABOVE FocusProvider (WS-ENT-3) so
        // every authenticated surface — including the FocusProvider-
        // scoped mentorship rails — can read the viewer's field-policy
        // via `useFieldPolicy()` / `<Gated>` without each surface
        // refetching the catalogue independently. One GET /api/me/
        // permissions/ per session; BE-driven 5-min stale-while-
        // revalidate (cache_max_age).
        // FocusProvider lifted to Layout (Phase 5a) so MentalityOverlay
        // can read the mentor's current focus and adapt its coaching
        // surface accordingly. Provider's state is in-memory only and
        // costs nothing on pages that never set focus.
        <PermissionsProvider>
        <FocusProvider>
        <div className="layout">
            {/* Skip-link for keyboard users — first Tab target on every
             *  page so they can bypass the sidebar nav and jump straight
             *  to the current tab's content. WCAG 2.1 AA (2.4.1). */}
            <a href="#main-content" className="skip-link">
                Skip to main content
            </a>

            {/* iOS 26 Liquid Glass: real elements for Safari toolbar tinting */}
            <div className="safari-tint-top" aria-hidden="true" />
            <div className="safari-tint-bottom" aria-hidden="true" />

            {/* Mobile overlay — closes sidebar when tapping outside */}
            {!sidebarCollapsed && (
                <div
                    className="sidebar-overlay"
                    onClick={() => toggleSidebar(true)}
                />
            )}
            <Sidebar
                activeTab={activeTab}
                onTabChange={onTabChange}
                isOpen={true}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => toggleSidebar(!sidebarCollapsed)}
                educationSubTab={educationSubTab}
                networkingSubTab={networkingSubTab}
                userData={userData}
                onChatSelect={onChatSelect}
                onNewDirectChat={onNewDirectChat}
                selectedChatId={selectedChatId}
                selectedProjectId={selectedProjectId}
            />
            <main id="main-content" className={`layout-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {/* NetworkIndicator + NotificationsBell are now rendered
                 *  inside every SurfaceHeader's actions slot (SSOT). No
                 *  floating global chrome in the Layout. */}
                {children({
                    showMenu: true,
                    onMenuClick: () => toggleSidebar(!sidebarCollapsed),
                })}
            </main>
            {/* M3 / M4 — Mentality overlay available on every page for
             * users with any mentorship role. Hidden for users without
             * one (no curated prompts → nothing to surface). */}
            {resolveRole(userData) && (
                // eslint-disable-next-line chiefy/no-naked-concept-strings -- ErrorBoundary scope is a captureError tag (infrastructure), not user copy.
                <ErrorBoundary scope="Mentality" fallback={() => null}>
                    <MentalityOverlay viewerRole={resolveRole(userData)!} />
                </ErrorBoundary>
            )}
            {/* WS-NAV-1 — Cmd-K palette. The lightweight launcher
             *  (above) owns the Cmd-K hotkey + isOpen state on the
             *  entry bundle so the heavy palette chunk (Fuse + indices
             *  + modal chrome) only loads on the FIRST press. While
             *  closed, this whole subtree mounts nothing — the lazy
             *  chunk is fetched at the moment cmdkLauncher.isOpen
             *  flips true. The `external` prop wires the palette's
             *  internal useCmdK to read isOpen + propagate close()
             *  through the launcher (no duplicate hotkey scope). */}
            {resolveRole(userData) && cmdkLauncher.isOpen && (
                <Suspense fallback={null}>
                    <CmdKPalette
                        role={viewerRole}
                        mentees={emptyMentees}
                        onOpenGlossary={openGlossaryTerm}
                        external={cmdkExternal}
                    />
                </Suspense>
            )}
            {/* WS-FTUE-1 Phase 2 — Spotlight tour driver. Auto-opens
             *  the 'mentor-first-run' tour for fresh mentors who
             *  haven't completed the walkthrough yet. The cohort gate
             *  is here (mentor + flag); the driver self-gates on the
             *  completed sentinel + resume cursor so re-mounts after
             *  a finished walkthrough render nothing. HelpMenu's
             *  Replay button dispatches the driver replay event so
             *  the tour re-launches without needing this component to
             *  expose imperative controls. */}
            {mentorFirstRunEligible && (
                <ErrorBoundary scope="SpotlightTourDriver" fallback={() => null}>
                    <Suspense fallback={null}>
                        <SpotlightTourDriver tourId="mentor-first-run" autoOpen />
                    </Suspense>
                </ErrorBoundary>
            )}
            {/* WS-DISC-3 — Global Help Menu. Mounted at the layout
             *  top-level, outside the rail/content, so the pill
             *  survives route changes. The pill is always paired with
             *  the panel through the shared useHelpMenu controller so
             *  ARIA aria-expanded / aria-controls stay in lockstep. */}
            {helpMenuEnabled && (
                <ErrorBoundary scope="HelpMenu" fallback={() => null}>
                    <HelpPill
                        aria-expanded={helpMenu.anchorProps['aria-expanded']}
                        aria-controls={helpMenu.anchorProps['aria-controls']}
                        onClick={helpMenu.toggle}
                    />
                    <Suspense fallback={null}>
                        <HelpMenuPanel
                            controller={helpMenu}
                            {...(helpOnboardingRole !== undefined
                                ? { onboardingRole: helpOnboardingRole }
                                : {})}
                            {...(userData?.id !== undefined
                                ? { userId: userData.id }
                                : {})}
                        />
                    </Suspense>
                </ErrorBoundary>
            )}
        </div>
        </FocusProvider>
        </PermissionsProvider>
    );
}

export default Layout;
